import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Loader, Card, EmptyState, PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Trash2, ChevronUp, ChevronDown, Save, GripVertical } from "lucide-react";

const blankItem = () => ({ name: "", category: "General", instructions: "", required: true, order: 0 });

export default function ChecklistTemplates() {
  const [templates, setTemplates] = useState(null);
  const [services, setServices] = useState([]);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/checklist-templates").then((r) => { setTemplates(r.data); }).catch(() => setTemplates([]));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setServices(r.data.services || [])).catch(() => {});
  }, []);

  const select = (t) => { setSelId(t.id); setDraft({ ...t, items: (t.items || []).map((i, idx) => ({ ...blankItem(), ...i, order: idx })) }); };

  const setItem = (idx, patch) => setDraft((d) => ({ ...d, items: d.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setDraft((d) => ({ ...d, items: [...d.items, { ...blankItem(), order: d.items.length }] }));
  const delItem = (idx) => setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
  const move = (idx, dir) => setDraft((d) => {
    const items = [...d.items]; const j = idx + dir;
    if (j < 0 || j >= items.length) return d;
    [items[idx], items[j]] = [items[j], items[idx]];
    return { ...d, items: items.map((it, i) => ({ ...it, order: i })) };
  });

  const save = async () => {
    if (!draft.name) { toast.error("Template name required"); return; }
    setSaving(true);
    try {
      await api.put(`/checklist-templates/${draft.id}`, { name: draft.name, service_type: draft.service_type, items: draft.items });
      toast.success("Template saved"); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this template? Applications already using it are not affected.")) return;
    try { await api.delete(`/checklist-templates/${draft.id}`); toast.success("Template deleted"); setSelId(null); setDraft(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Checklist Templates" subtitle="Reusable document checklists you can apply to any case — no code changes needed.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-template-btn" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> New Template</button>
          </DialogTrigger>
          <CreateTemplate services={services} onDone={(t) => { setOpen(false); load().then(() => t && select(t)); }} />
        </Dialog>
      </PageHeader>

      {!templates ? <Loader /> : templates.length === 0 ? (
        <Card className="p-6"><EmptyState icon={ClipboardList} title="No templates yet" description="Create a reusable checklist for a service like PGWP or Study Permit." /></Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="space-y-2 lg:col-span-1">
            {templates.map((t) => (
              <Card key={t.id} data-testid={`tpl-row-${t.id}`} onClick={() => select(t)} className={`p-4 cursor-pointer transition-shadow hover:shadow-md ${selId === t.id ? "ring-2 ring-primary/40" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><ClipboardList className="h-4 w-4 text-primary" /></div>
                  <div className="min-w-0">
                    <div className="font-head font-bold text-slate-900 truncate">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.service_type} · {(t.items || []).length} items</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="lg:col-span-2">
            {!draft ? (
              <Card className="p-10 text-center text-slate-400 text-sm">Select a template to edit its checklist.</Card>
            ) : (
              <Card className="p-5">
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div><label className="text-xs font-medium text-slate-500">Template name</label>
                    <input data-testid="edit-template-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
                  <div><label className="text-xs font-medium text-slate-500">Service type</label>
                    <select value={draft.service_type} onChange={(e) => setDraft({ ...draft, service_type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm">
                      {services.map((s) => <option key={s}>{s}</option>)}
                    </select></div>
                </div>

                <div className="space-y-2">
                  {draft.items.map((it, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50/50">
                      <div className="flex flex-col text-slate-300 pt-1">
                        <button onClick={() => move(idx, -1)} className="hover:text-slate-600"><ChevronUp className="h-4 w-4" /></button>
                        <GripVertical className="h-4 w-4" />
                        <button onClick={() => move(idx, 1)} className="hover:text-slate-600"><ChevronDown className="h-4 w-4" /></button>
                      </div>
                      <div className="flex-1 grid gap-2">
                        <div className="grid sm:grid-cols-2 gap-2">
                          <input data-testid={`item-name-${idx}`} placeholder="Document name" value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                          <input placeholder="Category" value={it.category} onChange={(e) => setItem(idx, { category: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        </div>
                        <input placeholder="Instructions (optional)" value={it.instructions} onChange={(e) => setItem(idx, { instructions: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={it.required} onChange={(e) => setItem(idx, { required: e.target.checked })} /> Required</label>
                      </div>
                      <button onClick={() => delItem(idx)} data-testid={`del-item-${idx}`} className="text-slate-300 hover:text-rose-500 pt-1"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>

                <button onClick={addItem} data-testid="add-item-btn" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary"><Plus className="h-4 w-4" /> Add document</button>

                <div className="flex gap-2 mt-5 pt-4 border-t border-slate-100">
                  <button onClick={save} disabled={saving} data-testid="save-template-btn" className="flex-1 bg-primary text-white px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save template"}</button>
                  <button onClick={remove} data-testid="delete-template-btn" className="px-4 py-2.5 border border-rose-200 text-rose-600 rounded-xl font-semibold text-sm flex items-center gap-2"><Trash2 className="h-4 w-4" /> Delete</button>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTemplate({ services, onDone }) {
  const [f, setF] = useState({ name: "", service_type: services[0] || "Other" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!f.service_type && services[0]) setF((p) => ({ ...p, service_type: services[0] })); }, [services]);
  const create = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    setSaving(true);
    try { const { data } = await api.post("/checklist-templates", { ...f, items: [] }); toast.success("Template created"); onDone(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader><DialogTitle>New Checklist Template</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><label className="text-xs font-medium text-slate-500">Template name</label>
          <input data-testid="new-template-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Study Permit Checklist" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" /></div>
        <div><label className="text-xs font-medium text-slate-500">Service type</label>
          <select data-testid="new-template-service" value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm">
            {services.map((s) => <option key={s}>{s}</option>)}
          </select></div>
        <DialogFooter><button data-testid="new-template-submit" onClick={create} disabled={saving} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm w-full disabled:opacity-60">{saving ? "Creating..." : "Create & edit items"}</button></DialogFooter>
      </div>
    </DialogContent>
  );
}
